import React, { useCallback, useEffect, useMemo, useState } from 'react'

import cx from 'classnames'

import { Input } from '@driftt/tide-core'

import { fetchVideoDetails } from 'api'
import { driftLoadingStateBuilder } from 'components/States'
import { parseVTT, TranscriptEntry } from 'utils/transcript'

import { EntityMention,getAllMentions, MeetingRecordingInsights } from '../insights'

import styles from './styles.module.css'


const TRANSCRIPT_FILE_TYPE = 'text/vtt'

interface MeetingTranscriptProps {
  videoId: number
  insights?: MeetingRecordingInsights
}

const SPEAKER_NUMBER_STYLES = [
  styles.speakerNumberOne,
  styles.speakerNumberTwo,
  styles.speakerNumberThree,
  styles.speakerNumberFour,
  styles.speakerNumberFive
]

const UtteranceWithMentions: React.FC<{ 
  utterance: string, 
  mentions?: EntityMention[] 
}> = ({ utterance, mentions}) => {
  const mention = mentions?.find(mention => mention.text === utterance)

  if (mention) {
    const start = utterance.indexOf(mention.entity)
    const end = start + mention.entity.length
    return <p>
      {utterance.substring(0, start)}
      <span className={styles.entityMention}>{utterance.substring(start, end)}</span>
      {utterance.substring(end)}
    </p>
  }

  return <p>{utterance}</p>
}

const MeetingTranscript: React.FC<MeetingTranscriptProps> = ({ videoId, insights }) => {

  const [search, setSearch] = useState<string>()
  const [video, setVideo] = useState<any>()
  const transcriptSource = useMemo(() => 
    video?.videoSources?.find((source: any) => source.type === TRANSCRIPT_FILE_TYPE)?.src
  , [video])

  const [transcript, setTranscript] = useState<TranscriptEntry[]>()
  const [isError, setIsError] = useState<boolean>(false)
  const filteredTranscript = useMemo(() => transcript?.filter(line => 
    !search || line.utterance.toLowerCase().includes(search.toLowerCase()))
  , [search, transcript])

  const mentions = useMemo(() => getAllMentions(insights), [insights])

  const handleSearchChange = useCallback((e: any) => {
    setSearch(e.target.value)
  }, [])

  useEffect(() => {
    if (transcriptSource) {
      fetch(transcriptSource)
        .then(response => response.text())
        .then(data => {
          setTranscript(parseVTT(data))
        })
        .catch(() => setIsError(true))
    } else if (video) {
      setIsError(true)
    }
  }, [video, transcriptSource])

  useEffect(() => {
    fetchVideoDetails(videoId).then(setVideo)
  }, [videoId])

  if (isError) {
    return <p className={styles.emptyText}>Could not find a transcript for this recording</p>
  }

  const LoadingComponent = driftLoadingStateBuilder('Loading transcript...')

  if (!transcript) {
    return <LoadingComponent />
  }

  return (
    <div className={styles.transcriptContainer}>
      <Input
        className={styles.searchInput}
        type="search"
        placeholder="Search call..."
        size="small"
        defaultValue={search}
        onChange={handleSearchChange}
      />
      <div className={styles.transcript}>
        {filteredTranscript?.map((entry, i) =>
          <div key={i} className={styles.transcriptLine}>
            <div className={cx(styles.speakerAndTime, SPEAKER_NUMBER_STYLES[entry.speakerNum])}>
              <p className={styles.speakerName}>{entry.speakerName}</p>
              <p className={styles.timestamp}>{entry.timestamp}</p>
            </div>
            <div className={styles.utterance}>
                <UtteranceWithMentions utterance={entry.utterance} mentions={mentions}/>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MeetingTranscript